import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { locationsApi, type Location } from '@/services/locationsApi';
import { useAuth } from './AuthContext';
import { queryClient } from '@/queryClient';

interface LocationContextType {
  locations: Location[];
  selectedLocation: Location | null;
  setSelectedLocation: (location: Location | null) => void;
  locationScopeKey: string;
  canSwitchLocations: boolean;
  isLoading: boolean;
  refreshLocations: () => Promise<void>;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

const STORAGE_KEY = 'pos:selectedLocationId';
const MULTI_LOCATION_ROLES = new Set(['OWNER']);
const LOCATION_SCOPE_CHANGED_EVENT = 'pos:location-scope-changed';

export const LocationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocation, setSelectedLocationState] = useState<Location | null>(null);
  const canSwitchLocations = Boolean(user?.role && MULTI_LOCATION_ROLES.has(user.role));
  const assignedLocationId = (user as any)?.locationId as string | undefined;
  const [isLoading, setIsLoading] = useState(true);
  const locationScopeKey = selectedLocation?.id ?? assignedLocationId ?? '';

  const fetchLocations = async () => {
    try {
      setIsLoading(true);
      const data = await locationsApi.getAll();
      const activeLocations = data.filter((loc) => loc.isActive);
      setLocations(activeLocations);

      if (activeLocations.length > 0) {
        const storedLocationId = localStorage.getItem(STORAGE_KEY);
        let defaultLocation = null;

        if (storedLocationId) {
          const storedLoc = activeLocations.find((loc) => loc.id === storedLocationId);
          if (storedLoc && (canSwitchLocations || storedLoc.id === assignedLocationId)) {
            defaultLocation = storedLoc;
          }
        }

        if (!defaultLocation && assignedLocationId) {
          defaultLocation = activeLocations.find((loc) => loc.id === assignedLocationId);
        }

        if (!defaultLocation) {
          defaultLocation = activeLocations.find((l) => l.isMainBranch) || activeLocations[0];
        }

        setSelectedLocationState(defaultLocation);
        if (defaultLocation) {
          localStorage.setItem(STORAGE_KEY, defaultLocation.id);
        }
      } else {
        setSelectedLocationState(null);
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      setLocations([]);
      setSelectedLocationState(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchLocations();
    } else {
      setLocations([]);
      setSelectedLocationState(null);
      setIsLoading(false);
    }
  }, [user, assignedLocationId, canSwitchLocations]);

  const setSelectedLocation = (location: Location | null) => {
    const nextLocation = location ?? selectedLocation ?? locations.find(l => l.isMainBranch) ?? locations[0] ?? null;
    if (!nextLocation) {
      return;
    }

    const requiredLocationId = assignedLocationId ?? selectedLocation?.id;
    if (!canSwitchLocations && requiredLocationId && nextLocation.id !== requiredLocationId) {
      return;
    }

    setSelectedLocationState(nextLocation);
    localStorage.setItem(STORAGE_KEY, nextLocation.id);
    window.dispatchEvent(new CustomEvent(LOCATION_SCOPE_CHANGED_EVENT, {
      detail: { locationId: nextLocation.id },
    }));
    setTimeout(() => {
      void queryClient.invalidateQueries();
    }, 50);
  };

  return (
    <LocationContext.Provider
      value={{
        locations,
        selectedLocation,
        setSelectedLocation,
        locationScopeKey,
        canSwitchLocations,
        isLoading,
        refreshLocations: fetchLocations,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};

export const useLocationContext = () => {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error('useLocationContext must be used within a LocationProvider');
  }
  return context;
};
